'use client';

/**
 * ExcursionWizard — telaio multi-step per prenotazione escursione.
 *
 * Vive dentro il Modal esterno di UnifiedBookingModal — nessun modale annidato.
 * Il footer nav usa `sticky bottom-0` + margini negativi per agganciarsi
 * ai bordi del body modale (bodyClassName="p-4 md:p-6").
 *
 * Phase 'form'   → wizard multi-step (quando / partecipanti / extra / riepilogo / dati)
 * Phase 'paying' → StepPayment in-modale (loading → simulate|redirect → confirmed)
 */

import { useState, useMemo, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Check } from 'lucide-react';
import { useAuth } from '@/app/providers';
import type { ExcursionAsset } from '@/components/booking/UnifiedBookingModal';
import StepWhen from '@/components/booking/excursion/steps/StepWhen';
import StepParticipants from '@/components/booking/excursion/steps/StepParticipants';
import StepExtras from '@/components/booking/excursion/steps/StepExtras';
import StepSummary from '@/components/booking/excursion/steps/StepSummary';
import StepCustomer from '@/components/booking/excursion/steps/StepCustomer';
import StepPayment, { type PaymentStatus } from '@/components/booking/excursion/steps/StepPayment';
import { computeExcursionPricing } from '@/lib/excursion-wizard-pricing';

/* ── Wizard state ─────────────────────────────────────────────────────────── */

export interface ExcursionWizardState {
  date: string | null;
  slotId: string | null;
  departureTime: string | null;
  slotRemaining: number | null;
  participants: number;
  tierKey: string | null;
  extras: string[];
  customer: { phone?: string; notes?: string };
}

const INITIAL_STATE: ExcursionWizardState = {
  date: null,
  slotId: null,
  departureTime: null,
  slotRemaining: null,
  participants: 1,
  tierKey: null,
  extras: [],
  customer: {},
};

/* ── Payment state ────────────────────────────────────────────────────────── */

interface PayState {
  status: PaymentStatus;
  errorMsg: string | null;
  errorCode: string | null;
  bookingId: string | null;
}

const INITIAL_PAY_STATE: PayState = {
  status: 'loading',
  errorMsg: null,
  errorCode: null,
  bookingId: null,
};

/* ── Step registry ────────────────────────────────────────────────────────── */

interface StepDef {
  key: string;
  label: string;
}

const STEPS: StepDef[] = [
  { key: 'when',         label: 'Quando' },
  { key: 'participants', label: 'Partecipanti' },
  { key: 'extras',       label: 'Extra' },
  { key: 'summary',      label: 'Riepilogo' },
  { key: 'customer',     label: 'Dati' },
];

/* ── Progress bar ─────────────────────────────────────────────────────────── */

function WizardProgress({
  steps,
  currentIndex,
}: {
  steps: StepDef[];
  currentIndex: number;
}) {
  return (
    <div
      data-cid="excursion-wizard-progress"
      className="flex items-start"
      role="list"
      aria-label="Step prenotazione"
    >
      {steps.map((step, i) => {
        const isCompleted = i < currentIndex;
        const isCurrent   = i === currentIndex;

        return (
          <div key={step.key} className="flex items-center flex-1 min-w-0" role="listitem">
            {/* Bullet + label */}
            <div className="flex flex-col items-center gap-1 flex-1 min-w-0 px-0.5">
              <div
                aria-current={isCurrent ? 'step' : undefined}
                className={[
                  'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors',
                  isCompleted ? 'bg-sky-500 text-white'                                   : '',
                  isCurrent   ? 'bg-sky-600 text-white ring-2 ring-sky-300 ring-offset-1' : '',
                  !isCompleted && !isCurrent ? 'bg-slate-100 text-slate-400'              : '',
                ].join(' ')}
              >
                {isCompleted ? <Check size={13} strokeWidth={2.5} /> : i + 1}
              </div>
              <span
                className={[
                  'text-[10px] leading-tight text-center truncate w-full',
                  isCurrent   ? 'font-bold text-sky-700' : '',
                  isCompleted ? 'text-sky-500'           : '',
                  !isCompleted && !isCurrent ? 'text-slate-400' : '',
                ].join(' ')}
              >
                {step.label}
              </span>
            </div>

            {/* Connector (non mostrato dopo l'ultimo step) */}
            {i < steps.length - 1 && (
              <div
                className={[
                  'h-px flex-1 shrink mx-1 mb-4 transition-colors',
                  i < currentIndex ? 'bg-sky-400' : 'bg-slate-200',
                ].join(' ')}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── ExcursionWizard ──────────────────────────────────────────────────────── */

interface ExcursionWizardProps {
  asset: ExcursionAsset;
}

export default function ExcursionWizard({ asset }: ExcursionWizardProps) {
  const router = useRouter();
  const { user } = useAuth();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [state, setState] = useState<ExcursionWizardState>(INITIAL_STATE);
  const [phase, setPhase] = useState<'form' | 'paying'>('form');
  const [payState, setPayState] = useState<PayState>(INITIAL_PAY_STATE);

  /* ── Auto-include mandatory extras ─────────────────────────────────────── */
  useEffect(() => {
    const mandatoryKeys = (asset.extras ?? [])
      .filter((e) => e.mandatory)
      .map((e) => e.key);
    if (mandatoryKeys.length === 0) return;
    setState((s) => {
      const missing = mandatoryKeys.filter((k) => !s.extras.includes(k));
      if (missing.length === 0) return s;
      return { ...s, extras: [...s.extras, ...missing] };
    });
  }, [asset.extras]);

  /* ── Step list effettiva (extras rimosso se nessun opzionale) ───────────── */
  const activeSteps = useMemo(() => {
    const hasOptionalExtras = (asset.extras ?? []).some((e) => !e.mandatory);
    return hasOptionalExtras ? STEPS : STEPS.filter((s) => s.key !== 'extras');
  }, [asset.extras]);

  const currentStep = activeSteps[currentIndex] ?? activeSteps[activeSteps.length - 1];
  const isFirst     = currentIndex === 0;
  const isLast      = currentIndex === activeSteps.length - 1;

  const canNext = useMemo(() => {
    switch (currentStep.key) {
      case 'when':
        return !!state.date && !!state.departureTime;
      case 'participants': {
        const isTimedMode =
          asset.pricingMode === 'tiered' || asset.pricingMode === 'exclusive';
        if (isTimedMode) return !!state.tierKey;
        const maxP = Math.min(
          state.slotRemaining ?? asset.maxCapacity ?? 99,
          asset.maxCapacity ?? 99,
        );
        return state.participants >= 1 && state.participants <= maxP;
      }
      case 'extras':
        return true;
      case 'customer':
        return !!(state.customer.phone && state.customer.phone.trim());
      default:
        return true;
    }
  }, [
    currentStep.key,
    state.date,
    state.departureTime,
    state.tierKey,
    state.participants,
    state.slotRemaining,
    state.customer.phone,
    asset.pricingMode,
    asset.maxCapacity,
  ]);

  const handleBack = () => {
    if (!isFirst) setCurrentIndex((i) => i - 1);
  };

  /* ── Submit + payment flow ──────────────────────────────────────────────── */

  const handleBookNow = async () => {
    /* Gate Fase 1: utente non loggato → login */
    if (!user) {
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      router.push(`/login?returnUrl=${returnUrl}`);
      return;
    }

    /* Entra in fase pagamento con loading */
    setPhase('paying');
    setPayState({ status: 'loading', errorMsg: null, errorCode: null, bookingId: null });

    const { total, pricingTierPayload } = computeExcursionPricing(asset, state);

    try {
      /* ── 1. Crea prenotazione ──────────────────────────────────────────── */
      const res = await fetch('/api/excursion-bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          excursion_id:   asset.id,
          slot_id:        state.slotId || null,
          departure_date: state.date,
          departure_time: state.departureTime,
          people:         state.participants,
          guest_name:     user.name,
          guest_email:    user.email,
          guest_phone:    state.customer.phone?.trim() || null,
          notes:          state.customer.notes?.trim() || null,
          pricing_tier:   pricingTierPayload,
          extras:         state.extras,
          total_amount:   total,
        }),
      });
      const j = (await res.json()) as {
        success?: boolean;
        code?: string;
        error?: string;
        message?: string;
        data?: { id: string };
      };

      if (!res.ok || !j.success) {
        if (j.code === 'KYC_PHONE_REQUIRED' || j.code === 'KYC_DOC_REQUIRED') {
          setPayState({
            status: 'error',
            errorMsg: j.error ?? 'Verifica KYC richiesta prima di prenotare.',
            errorCode: j.code,
            bookingId: null,
          });
          return;
        }
        if (res.status === 409) {
          setPayState({
            status: 'error',
            errorMsg: 'Posti non più disponibili per questa data. Scegli un\'altra data.',
            errorCode: 'CAPACITY_EXCEEDED',
            bookingId: null,
          });
          return;
        }
        if (res.status === 401) {
          const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
          router.push(`/login?returnUrl=${returnUrl}`);
          return;
        }
        throw new Error(j.error || j.message || `HTTP ${res.status}`);
      }

      const bookingId = j.data?.id ?? '';

      /* ── 2. Avvia pagamento ─────────────────────────────────────────────── */
      const payRes = await fetch('/api/excursion-payments/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ excursion_booking_id: bookingId }),
      });
      const pj = (await payRes.json().catch(() => ({}))) as {
        simulated?: boolean;
        paymentUrl?: string;
        error?: string;
      };

      /* Pagamento simulato (staging MOLLIE_FORCE_SIMULATE=true) */
      if (payRes.ok && pj.simulated) {
        setPayState({ status: 'simulate', errorMsg: null, errorCode: null, bookingId });
        return;
      }

      /* Pagamento reale: redirect Mollie */
      if (payRes.ok && pj.paymentUrl) {
        setPayState({ status: 'redirecting', errorMsg: null, errorCode: null, bookingId });
        // TODO: qui andrà Mollie Components (campi carta in-modale) quando MOLLIE_PROFILE sarà configurata
        window.location.href = pj.paymentUrl;
        return;
      }

      throw new Error(pj.error ?? 'Pagamento non avviato.');
    } catch (err) {
      setPayState({
        status: 'error',
        errorMsg: (err as Error).message || 'Errore imprevisto. Riprova.',
        errorCode: null,
        bookingId: null,
      });
    }
  };

  /* ── Pagamento simulato: chiama /simulate e va a /booking/confirmed ─────── */

  const handleSimulatePay = async (outcome: 'paid' | 'failed') => {
    const bookingId = payState.bookingId;
    if (!bookingId) return;

    setPayState((s) => ({ ...s, status: 'loading' }));
    try {
      const res = await fetch('/api/excursion-payments/simulate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ excursion_booking_id: bookingId, status: outcome }),
      });
      const j = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string };
      if (res.ok && j.success !== false) {
        router.push(`/booking/confirmed?excursion_booking_id=${bookingId}`);
        return;
      }
      throw new Error(j.error ?? `HTTP ${res.status}`);
    } catch (err) {
      setPayState((s) => ({
        ...s,
        status: 'error',
        errorMsg: (err as Error).message || 'Errore nel pagamento simulato.',
        errorCode: null,
      }));
    }
  };

  const handleNext = () => {
    if (isLast) {
      void handleBookNow();
      return;
    }
    setCurrentIndex((i) => i + 1);
  };

  /* ── Render ─────────────────────────────────────────────────────────────── */

  /* Fase pagamento: mostra StepPayment in-modale, niente progress bar né nav */
  if (phase === 'paying') {
    return (
      <div data-cid="excursion-wizard" className="flex flex-col gap-2">
        <StepPayment
          status={payState.status}
          errorMsg={payState.errorMsg}
          errorCode={payState.errorCode}
          bookingId={payState.bookingId}
          onSimulatePay={handleSimulatePay}
          onBack={() => setPhase('form')}
        />
      </div>
    );
  }

  /* Fase form: wizard multi-step */
  return (
    <div data-cid="excursion-wizard" className="flex flex-col gap-2">
      {/* Progress orizzontale */}
      <WizardProgress steps={activeSteps} currentIndex={currentIndex} />

      <hr className="border-slate-100" />

      {/* Step corrente — pb per non finire sotto il footer sticky */}
      <div className="flex-1 pb-20">
        {currentStep.key === 'when' ? (
          <StepWhen asset={asset} state={state} setState={setState} />
        ) : currentStep.key === 'participants' ? (
          <StepParticipants asset={asset} state={state} setState={setState} />
        ) : currentStep.key === 'extras' ? (
          <StepExtras asset={asset} state={state} setState={setState} />
        ) : currentStep.key === 'summary' ? (
          <StepSummary asset={asset} state={state} setState={setState} />
        ) : currentStep.key === 'customer' ? (
          <StepCustomer asset={asset} state={state} setState={setState} />
        ) : null}
      </div>

      {/* Footer nav sticky — margini negativi per raggiungere i bordi del body (p-4 md:p-6) */}
      <div
        data-cid="excursion-wizard-nav"
        className="sticky bottom-0 bg-white border-t border-slate-200 flex items-center justify-between gap-3 py-3 -mx-4 px-4 md:-mx-6 md:px-6 -mb-4 md:-mb-6"
      >
        <button
          type="button"
          data-cid="excursion-wizard-back"
          disabled={isFirst}
          onClick={handleBack}
          className="px-5 py-2.5 text-sm font-semibold rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          Indietro
        </button>

        <button
          type="button"
          data-cid="excursion-wizard-next"
          disabled={!canNext}
          onClick={handleNext}
          className="px-6 py-2.5 text-sm font-bold rounded-full bg-sky-600 text-white hover:bg-sky-700 shadow-md disabled:opacity-50 disabled:shadow-none transition-all"
        >
          {isLast ? 'Prenota' : 'Avanti'}
        </button>
      </div>
    </div>
  );
}
